/**
 * An area of the map as a URL carries it to the property list — `?area=suburb:1234` or
 * `?area=grid:<uuid>`.
 *
 * IN A MODULE OF ITS OWN, WITH NO IMPORTS, for the reason `located.ts` is: `/properties`
 * shape-checks its search in the eager route tree, and anything this file imported would ride
 * into the chunk every page downloads.
 *
 * ONE PARAMETER FOR BOTH KINDS, because they are one question — "the properties in this place" —
 * and the list should not grow a second filter, chip and empty state when a third kind of area
 * turns up.
 */
export type PropertyArea = { kind: 'suburb'; id: number } | { kind: 'grid'; id: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Shape-checked only. An id that matches no suburb or grid filters to nothing and says so. */
export function parseArea(value: unknown): PropertyArea | null {
  if (typeof value !== 'string') return null;
  const [kind, id, ...rest] = value.split(':');
  if (id === undefined || rest.length > 0) return null;
  if (kind === 'suburb') {
    const suburb = Number(id);
    return /^\d+$/.test(id) && Number.isSafeInteger(suburb) && suburb > 0
      ? { kind, id: suburb }
      : null;
  }
  if (kind === 'grid') return UUID.test(id) ? { kind, id: id.toLowerCase() } : null;
  return null;
}

export function formatArea(area: PropertyArea): string {
  return `${area.kind}:${area.id}`;
}
